import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Download, Hand, HeartPulse, LifeBuoy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { IMAGES, SOS_STEPS, GUIDES, HELPLINE_NOTE } from "../data/content";

const PHASES = [
  { name: "Breathe in", secs: 4, scale: 1.3 },
  { name: "Hold", secs: 7, scale: 1.3 },
  { name: "Breathe out", secs: 8, scale: 1 },
];

export default function QuickHeals() {
  // --- SOS grounding state ---
  const [step, setStep] = useState(-1);
  const [found, setFound] = useState(0);
  const cur = SOS_STEPS[step];
  const done = step === SOS_STEPS.length;

  const startSOS = () => {
    setStep(0);
    setFound(0);
  };

  const tapOne = () => {
    if (found + 1 >= cur.count) {
      setStep(step + 1);
      setFound(0);
    } else {
      setFound(found + 1);
    }
  };

  // --- Breathing state ---
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [count, setCount] = useState(PHASES[0].secs);
  const phase = PHASES[phaseIdx];

  useEffect(() => {
    if (!running) return;
    const t = setTimeout(() => {
      if (count > 1) setCount(count - 1);
      else {
        const next = (phaseIdx + 1) % PHASES.length;
        setPhaseIdx(next);
        setCount(PHASES[next].secs);
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [running, count, phaseIdx]);

  const toggleBreath = () => {
    if (running) {
      setRunning(false);
      setPhaseIdx(0);
      setCount(PHASES[0].secs);
    } else {
      setRunning(true);
    }
  };

  const download = (g) => {
    const blob = new Blob([g.body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = g.file;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${g.name} saved — keep it where you can reach it.`);
  };

  return (
    <div>
      <PageHero
        eyebrow="Quick Heals · free, always"
        lines={["When you need", "steady, right now."]}
        sub="Fast-action coping toolkits, micro groundings and pocket guides. No sign-up, no fee — just small, real relief in the middle of a hard moment."
        image={IMAGES.calmWindow}
        alt="A quiet moment of calm by a window"
      />

      {/* SOS Grounding */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <p className="eyebrow">SOS grounding · 5-4-3-2-1</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            Walk your five senses back into the room.
          </h2>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            data-testid="sos-widget"
            className="mx-auto mt-12 max-w-xl rounded-[2rem] border border-pine/10 bg-white/90 p-8 text-center shadow-[0_18px_50px_rgba(45,90,70,0.12)] sm:p-10"
          >
            {step === -1 && (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blush text-terra">
                  <LifeBuoy className="h-7 w-7" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">
                  Feeling spun up? Start where you sit.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-bodytext">
                  Five things you can see, four you can touch, three you can hear, two you
                  can smell, one you can taste. We'll count with you.
                </p>
                <button
                  data-testid="sos-start-button"
                  onClick={startSOS}
                  className="mt-8 rounded-full bg-terra px-8 py-3.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:brightness-105"
                >
                  Start the reset
                </button>
              </>
            )}

            {cur && (
              <>
                <div className="flex justify-center gap-1.5">
                  {SOS_STEPS.map((s, i) => (
                    <span
                      key={s.sense}
                      className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${
                        i < step ? "bg-sage" : i === step ? "bg-terra" : "bg-pine/15"
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-sage">
                  Step {step + 1} of {SOS_STEPS.length} · {cur.sense}
                </p>
                <p className="mt-4 font-serif text-4xl text-ink" data-testid="sos-counter">
                  <span className="text-terra">{cur.count - found}</span> to find
                </p>
                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-bodytext">
                  {cur.hint}
                </p>
                <button
                  data-testid="sos-tap-button"
                  onClick={tapOne}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-8 py-3.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:bg-pinedeep"
                >
                  <Hand className="h-4 w-4" /> I found one
                </button>
              </>
            )}

            {done && (
              <>
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sagelight text-pine">
                  <HeartPulse className="h-7 w-7" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">Well walked.</h3>
                <p className="mt-3 text-sm leading-relaxed text-bodytext">
                  Notice your breath — it's likely slower than two minutes ago. If the
                  spiral returns, run it again; the path gets shorter each time.
                </p>
                <button
                  data-testid="sos-reset-button"
                  onClick={startSOS}
                  className="mt-8 inline-flex items-center gap-2 rounded-full border border-pine/25 px-6 py-3 text-sm font-semibold text-pine hover:bg-sagelight/60"
                >
                  <RotateCcw className="h-4 w-4" /> Run it again
                </button>
              </>
            )}
          </div>
        </Reveal>
      </section>

      {/* Breathing */}
      <section className="bg-creamdeep py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">4-7-8 breathing</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
              A breath long enough to tell your body: stand down.
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-bodytext">
              Four counts in, seven held, eight out. Three or four rounds is usually
              enough to feel your shoulders argue less. Follow the circle.
            </p>
            <button
              data-testid="breathing-toggle-button"
              onClick={toggleBreath}
              className={`mt-8 rounded-full px-8 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                running
                  ? "border border-pine/25 text-pine hover:bg-sagelight/60"
                  : "bg-pine text-cream hover:bg-pinedeep"
              }`}
            >
              {running ? "Stop" : "Start breathing"}
            </button>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="relative mx-auto flex h-72 w-72 items-center justify-center sm:h-80 sm:w-80">
              <motion.div
                aria-hidden="true"
                animate={{ scale: running ? phase.scale : 1 }}
                transition={{ duration: running ? phase.secs : 0.8, ease: "easeInOut" }}
                className="flex h-44 w-44 items-center justify-center rounded-full bg-sagelight shadow-inner"
              >
                <span className="h-24 w-24 rounded-full bg-pine/10" />
              </motion.div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <p className="font-serif text-2xl italic text-pine" data-testid="breathing-phase-label">
                  {running ? phase.name : "Ready"}
                </p>
                <p className="mt-1 font-mono text-3xl text-pine" data-testid="breathing-count-label">
                  {running ? count : "4-7-8"}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Downloads */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <p className="eyebrow">Pocket guides</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            Self-soothe, saved to your phone.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {GUIDES.map((g, i) => (
            <Reveal key={g.name} delay={i * 0.08}>
              <div className="card-soft flex h-full flex-col p-7" data-testid={`guide-card-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blush text-terra">
                  <Download className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-serif text-2xl text-ink">{g.name}</h3>
                <p className="mt-2 flex-1 text-sm text-bodytext/80">{g.meta}</p>
                <button
                  data-testid={`guide-download-${i}`}
                  onClick={() => download(g)}
                  className="mt-6 flex items-center justify-center gap-2 rounded-full border border-pine/25 py-3 text-sm font-semibold text-pine transition-all hover:bg-pine hover:text-cream"
                >
                  <Download className="h-4 w-4" /> Download
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Crisis */}
      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <div
            data-testid="quickheals-crisis-card"
            className="rounded-[2rem] border border-terra/30 bg-blush/60 p-8 sm:p-10"
          >
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-terra text-cream">
                <LifeBuoy className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-serif text-2xl text-ink">If it's heavier than a bad day</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-bodytext">
                  {HELPLINE_NOTE} You can find a free, confidential helpline in almost
                  every country at{" "}
                  <a
                    data-testid="quickheals-helpline-link"
                    href="https://findahelpline.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-terra underline underline-offset-2"
                  >
                    findahelpline.com
                  </a>
                  . If you are in danger right now, call your local emergency number.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
